import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components.
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    const content = this.details.querySelector('.details-content');

    if (content instanceof HTMLElement) {
      return content;
    }

    return null;
  }

  /** @type {HTMLButtonElement | null} */
  get previewToggle() {
    const button = this.details.querySelector('[data-preview-toggle]');

    if (button instanceof HTMLButtonElement) {
      return button;
    }

    return null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #isPreviewRow() {
    return this.details.dataset.showPreview === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleSummaryClick, { signal });

    if (this.content) {
      this.content.addEventListener('click', this.handleContentClick, { signal });
    }

    if (this.previewToggle) {
      this.previewToggle.addEventListener('click', this.handlePreviewToggleClick, { signal });
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    this.#syncAccessibilityState();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles summary click.
   * Preview rows never allow native closing because closed <details> hides the preview.
   *
   * @param {Event} event
   */
  handleSummaryClick = (event) => {
    if (this.#isPreviewRow) {
      event.preventDefault();

      this.details.open = true;
      this.details.classList.toggle('is-preview-collapsed');

      this.#syncAccessibilityState();
      return;
    }

    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    queueMicrotask(() => {
      this.#syncAccessibilityState();
    });
  };

  /**
   * Expands when the user clicks the preview body.
   *
   * @param {Event} event
   */
  handleContentClick = (event) => {
    if (!this.#isPreviewRow) return;

    if (this.details.classList.contains('is-preview-collapsed')) {
      event.preventDefault();

      this.#expandPreview();
    }
  };

  /**
   * Handles explicit read more / show less button.
   *
   * @param {Event} event
   */
  handlePreviewToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#isPreviewRow) return;

    if (this.details.classList.contains('is-preview-collapsed')) {
      this.#expandPreview();
    } else {
      this.#collapsePreview();
    }
  };

  #expandPreview() {
    this.details.open = true;
    this.details.classList.remove('is-preview-collapsed');
    this.#syncAccessibilityState();
  }

  #collapsePreview() {
    this.details.open = true;
    this.details.classList.add('is-preview-collapsed');
    this.#syncAccessibilityState();
  }

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#syncAccessibilityState();
  };

  /**
   * Sets default open state.
   * Preview rows must remain open so the content can be clamped visually.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    if (this.#isPreviewRow) {
      const shouldStartExpanded =
        (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
        (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

      this.details.open = true;

      if (shouldStartExpanded) {
        this.details.classList.remove('is-preview-collapsed');
      } else if (!this.details.classList.contains('is-preview-collapsed')) {
        this.details.classList.add('is-preview-collapsed');
      }

      return;
    }

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      if (this.#isPreviewRow) {
        this.#collapsePreview();
      } else {
        this.details.open = false;
        this.#syncAccessibilityState();
      }

      this.summary.focus();
    }
  };

  #syncAccessibilityState() {
    if (this.#isPreviewRow) {
      const isExpanded = !this.details.classList.contains('is-preview-collapsed');

      this.summary.setAttribute('aria-expanded', String(isExpanded));

      if (this.previewToggle) {
        this.previewToggle.setAttribute('aria-expanded', String(isExpanded));
      }

      return;
    }

    this.summary.setAttribute('aria-expanded', String(this.details.open));
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}